import { useEffect } from "react"

import { atom, useAtom } from "jotai"
import { Provider as JotaiProvider } from "jotai"
import { useHydrateAtoms } from "jotai/utils"

import ProductTypeBooking from "components/ecom/product/booking"
import ProductTypeSimple from "components/ecom/product/simple"
import Layout from "components/Layout"
import SEO from "components/Seo"
import { prisma } from "lib/prisma"
import { slugify } from "utils/helpers"

import type { Product } from "@prisma/client"
import type {
  Image as PrismaImage,
  ProductAddon,
  ProductCrossSell,
  ProductVariant
} from "@prisma/client"
import type { GetStaticPaths, GetStaticProps, NextPage } from "next"

export type ProductAddonWithImages = ProductAddon & { images: PrismaImage[] }

export type ProductVariantWithImagesAndAddons = ProductVariant & {
  images: PrismaImage[]
  addons: ProductAddonWithImages[]
}

export type ProductPageProps = {
  product: Product & {
    images: PrismaImage[]
    addons: ProductAddonWithImages[]
    variants: ProductVariantWithImagesAndAddons[]
    crossSellsFrom: (ProductCrossSell & {
      crossSellTo: {
        title: string // include the title of the related crossSell
      } | null
    })[]
    crossSellsTo: (ProductCrossSell & {
      crossSellFrom: {
        title: string // include the title of the related crossSell
      } | null
    })[]
  }
  optionalExtrasContent: string | null
  installTimeContent: string | null
  isVATEnabled: boolean
  isDepositsEnabled: boolean
  depositPercentage: number
}

// store product page props in an atom so can access in sub-components
export const productPagePropsAtom = atom<ProductPageProps | null>(null)

const Page: NextPage<ProductPageProps> = ({
  product,
  optionalExtrasContent,
  installTimeContent,
  isVATEnabled,
  isDepositsEnabled,
  depositPercentage
}) => {
  useHydrateAtoms([
    [
      productPagePropsAtom,
      {
        product,
        optionalExtrasContent,
        installTimeContent,
        isVATEnabled,
        isDepositsEnabled,
        depositPercentage
      }
    ]
  ])

  const [, setProductPageProps] = useAtom(productPagePropsAtom)

  // need to setProductPageProps to latest data when the page has changed because useHydrateAtoms only runs on initial render
  useEffect(() => {
    setProductPageProps({
      product,
      optionalExtrasContent,
      installTimeContent,
      isVATEnabled,
      isDepositsEnabled,
      depositPercentage
    })
  }, [
    product,
    optionalExtrasContent,
    installTimeContent,
    isVATEnabled,
    isDepositsEnabled,
    depositPercentage,
    setProductPageProps
  ])
  return (
    <JotaiProvider scope={product.id}>
      <SEO
        pageTitle={product.title}
        titleTag={`${product.title} | Online Booking | ${process.env.NEXT_PUBLIC_SITE_NAME}`}
        metaDescription={product.title}
      />
      <Layout>
        {product.type === "BOOKING" ? (
          <ProductTypeBooking />
        ) : product.type === "HIDDEN" || product.type === "SIMPLE" ? (
          <ProductTypeSimple />
        ) : null}
      </Layout>
    </JotaiProvider>
  )
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  if (!params?.slug) return { notFound: true }
  const products = await prisma.product.findMany({
    where: {
      published: true,
      variants: {
        some: {}
      }
    },
    include: {
      images: {
        orderBy: {
          order: "asc"
        }
      },
      variants: {
        orderBy: {
          order: "asc"
        },
        include: {
          addons: {
            orderBy: {
              order: "asc"
            }
          }
        }
      },
      addons: {
        orderBy: {
          order: "asc"
        }
      },
      // get the title for use in the notification about which product can be cross-sold with this product
      crossSellsFrom: {
        include: {
          crossSellTo: {
            select: {
              title: true
            }
          }
        }
      },
      // get the title for use in the notification when configuring a crosssell
      crossSellsTo: {
        include: {
          crossSellFrom: {
            select: {
              title: true
            }
          }
        }
      }
    }
  })

  const product = products.find(
    product => slugify(product.title) === params.slug
  )

  const options = await prisma.option.findMany()

  if (!product || !product.variants || !options) return { notFound: true }

  // need to run through the variants and addons and:
  // 1. Retrieve the images from the imageIds props in variants, addons, and variant addons
  // 2. Restructure and nest the variants
  const variantsWithAttachedImages = await Promise.all(
    product.variants.map(async variant => {
      const images = await prisma.image.findMany({
        where: {
          id: {
            in: variant.imageIds
          }
        }
      })

      if (!variant.addons) return { ...variant, images }

      const addonsWithAttachedImages = await Promise.all(
        variant.addons.map(async addon => {
          const images = await prisma.image.findMany({
            where: {
              id: {
                in: addon.imageIds
              }
            }
          })

          return { ...addon, images }
        })
      )

      return { ...variant, images, addons: addonsWithAttachedImages }
    })
  )

  const addonsWithAttachedImages = await Promise.all(
    product.addons.map(async addon => {
      const images = await prisma.image.findMany({
        where: {
          id: {
            in: addon.imageIds
          }
        }
      })
      return {
        ...addon,
        images
      }
    })
  )

  product.variants = variantsWithAttachedImages
  product.addons = addonsWithAttachedImages

  const optionalExtrasContent =
    options?.find(o => o.name === "optionalExtrasContent")?.value || ""

  const installTimeContent =
    options?.find(o => o.name === "installTimeContent")?.value || ""

  const isVATEnabled = options.find(o => o.name === "enableVAT")?.value
  const isDepositsEnabled = options.find(
    o => o.name === "enableDeposits"
  )?.value
  const depositPercentage = options.find(
    o => o.name === "depositPercentage"
  )?.value

  return {
    props: {
      product: JSON.parse(JSON.stringify(product)),
      optionalExtrasContent: JSON.parse(JSON.stringify(optionalExtrasContent)),
      installTimeContent: JSON.parse(JSON.stringify(installTimeContent)),
      isVATEnabled: isVATEnabled === "true" ? true : false,
      isDepositsEnabled: isDepositsEnabled === "true" ? true : false,
      depositPercentage: parseInt(depositPercentage || "0")
    }
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const products = await prisma.product.findMany({
    where: {
      published: true
    },
    select: {
      title: true
    }
  })
  return {
    paths: products.map(post => ({
      params: {
        slug: slugify(post.title)
      }
    })),
    fallback: false
  }
}

export default Page
